import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { API_URL } from 'src/common/utils/api/fetchWrapper';
import type { ExportBrandingConfigData, ExportBrandingResponse } from '../types';

const brandingKey = (companyId: string) => ['admin', 'companies', companyId, 'export-branding'];

export const useExportBranding = (companyId: string) => {
  const { data, isLoading, isError, error, refetch } = useQuery<ExportBrandingResponse>({
    queryKey: brandingKey(companyId),
    enabled: !!companyId,
  });

  return { branding: data, isLoading, isError, error, refetch };
};

export const useUpdateExportBranding = (companyId: string) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<{ data: ExportBrandingResponse }, Error, ExportBrandingConfigData>({
    mutationFn: (input: ExportBrandingConfigData) =>
      api.put<ExportBrandingResponse>(`admin/companies/${companyId}/export-branding`, {
        config: input,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandingKey(companyId) });
    },
  });

  return {
    updateBranding: async (input: ExportBrandingConfigData) => mutateAsync(input),
    isLoading,
    error,
  };
};

export const useGenerateExportAssets = (companyId: string) => {
  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<{ data: { downloadUrl: string } }, Error, void>({
    mutationFn: () =>
      api.post<{ downloadUrl: string }>(`admin/companies/${companyId}/export-branding/generate`),
  });

  return { generate: async () => mutateAsync(), isLoading, error };
};

export const useDownloadExportAssets = (companyId: string) => {
  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<{ data: { downloadUrl: string } }, Error, void>({
    mutationFn: () =>
      api.get<{ downloadUrl: string }>(`admin/companies/${companyId}/export-branding/download`),
  });

  return { download: async () => mutateAsync(), isLoading, error };
};

export const usePreviewExportAssets = (companyId: string) => {
  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<void, Error, void>({
    mutationFn: async () => {
      const response = await fetch(
        `${API_URL}/admin/companies/${companyId}/export-branding/preview`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new Error(`Preview failed: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'export_assets_preview.docx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });

  return { preview: async () => mutateAsync(), isLoading, error };
};

export const useUploadLogo = (companyId: string) => {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<{ location: string }, Error, { logoType: string; file: File }>({
    mutationFn: async ({ logoType, file }) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${API_URL}/admin/companies/${companyId}/export-branding/logo/${logoType}`,
        {
          method: 'POST',
          body: formData,
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: brandingKey(companyId) });
    },
  });

  return {
    uploadLogo: async (logoType: string, file: File) => mutateAsync({ logoType, file }),
    isLoading,
    error,
  };
};

export const useDownloadTitlePageTemplate = (companyId: string) => {
  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<void, Error, void>({
    mutationFn: async () => {
      const response = await fetch(
        `${API_URL}/admin/companies/${companyId}/export-branding/title-page/template`,
        { method: 'POST', credentials: 'include' },
      );
      if (!response.ok) throw new Error(`Template download failed: ${response.statusText}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'title_page_template.docx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });

  return { downloadTemplate: async () => mutateAsync(), isLoading, error };
};

export const useUploadTitlePage = (companyId: string) => {
  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<{ location: string }, Error, File>({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(
        `${API_URL}/admin/companies/${companyId}/export-branding/title-page/upload`,
        { method: 'POST', body: formData, credentials: 'include' },
      );
      if (!response.ok) throw new Error(`Upload failed: ${response.statusText}`);
      return response.json();
    },
  });

  return { uploadFile: async (file: File) => mutateAsync(file), isLoading, error };
};

export const useDownloadDeployedTitlePage = (companyId: string) => {
  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<{ data: { downloadUrl: string } }, Error, void>({
    mutationFn: () =>
      api.get<{ downloadUrl: string }>(
        `admin/companies/${companyId}/export-branding/title-page/download`,
      ),
  });

  return { download: async () => mutateAsync(), isLoading, error };
};

export const useUploadExportAssetsFile = (companyId: string) => {
  const {
    mutateAsync,
    isPending: isLoading,
    error,
  } = useMutation<{ location: string }, Error, File>({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/admin/companies/${companyId}/export-assets`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      return response.json();
    },
  });

  return { uploadFile: async (file: File) => mutateAsync(file), isLoading, error };
};
